/**
 * Clone one top-level clipboard task, minting new ids.
 * Dependencies stay raw until remapDependencies runs on the whole set.
 */

import { normalizeTaskEstimatedDuration } from '../../../../../../utils/taskDuration';
import { syncEstimatedMinutesFromClipboardPayload } from '../../../../../../utils/migrations/legacyEstimatedDuration.js';
import { generateId } from '../constants';
import { newReminderId, normalizeReminders, restartRecurringReminder } from '../../../../../../utils/missionReminders';
import { DEFAULT_OPTIONS } from '../../../TaskNode/DecisionOptionsModal';
import { cloneAttachedDocsFromClipboard, cloneMissionVariableValues } from './payload';

/**
 * @param {Object} clipboardTask - Task from clipboard (v2 format)
 * @param {Map} idMap - Map of clipboard task index -> new task ID
 * @param {Map} optionIdMap - Map of "taskIndex-optionIndex" -> new option ID
 * @param {number} timestamp - Timestamp for ID generation
 * @param {number} index - Task index in clipboard array
 * @param {Map} customFieldIdMap - Map of clipboard field index -> new field ID
 * @param {Map} customFieldOptionIdMap - Map of "fieldIndex-optionIndex" -> new option ID
 */
export function cloneTaskFromClipboard(clipboardTask, idMap, optionIdMap, timestamp, index, customFieldIdMap = new Map(), customFieldOptionIdMap = new Map()) {
  const newId = generateId('task', timestamp, index);
  idMap.set(index, newId);

  // Clone subtasks with new IDs
  const clonedSubtasks = (clipboardTask.subtasks || []).map((subtask, idx) => ({
    ...subtask,
    id: generateId('subtask', timestamp, index, idx),
    completed: subtask.completed || false,
  }));

  // Clone deliverables with new IDs
  const clonedDeliverables = (clipboardTask.deliverables || []).map((d, idx) => ({
    ...d,
    id: generateId('deliverable', timestamp, index, idx),
    completed: d.completed || false,
  }));

  // Clone inputs with new IDs
  const clonedInputs = (clipboardTask.inputs || []).map((input, idx) => ({
    ...input,
    id: generateId('input', timestamp, index, idx),
    sourceType: 'external',
    completed: input.completed || false,
  }));

  // Clone options for decision nodes and build option ID map
  // Fall back to DEFAULT_OPTIONS so branch references in conditions can be remapped
  let clonedOptions;
  const nodeType = clipboardTask.nodeType || 'task';
  if (nodeType === 'decision') {
    const sourceOptions = clipboardTask.options?.length > 0 ? clipboardTask.options : DEFAULT_OPTIONS;
    clonedOptions = sourceOptions.map((opt, idx) => {
      const newOptId = generateId('option', timestamp, index, idx);
      optionIdMap.set(`${index}-${idx}`, newOptId);
      return {
        id: newOptId,
        label: opt.label,
        color: opt.color,
      };
    });
  }

  // Remap custom field values from indices to new IDs
  // From: { "0": 2 } -> { "cf-newid": "opt-newid" }
  let remappedCustomFieldValues;
  if (clipboardTask.customFieldValues && Object.keys(clipboardTask.customFieldValues).length > 0) {
    remappedCustomFieldValues = {};
    for (const [fieldIndexStr, value] of Object.entries(clipboardTask.customFieldValues)) {
      const fieldIndex = parseInt(fieldIndexStr, 10);
      const newFieldId = customFieldIdMap.get(fieldIndex);
      if (!newFieldId) continue; // field not mapped

      // For select fields (value is an option index), remap to new option ID
      if (typeof value === 'number') {
        const newOptionId = customFieldOptionIdMap.get(`${fieldIndex}-${value}`);
        if (newOptionId) {
          remappedCustomFieldValues[newFieldId] = newOptionId;
        }
      } else {
        // For other field types, keep value as-is
        remappedCustomFieldValues[newFieldId] = value;
      }
    }
    if (Object.keys(remappedCustomFieldValues).length === 0) {
      remappedCustomFieldValues = undefined;
    }
  }

  // Extract position from [x, y] tuple
  const pos = clipboardTask.pos || [0, 0];

  // Tags array - first tag is the primary/category
  const tags = clipboardTask.tags || [];
  // Derive category from first tag (for backwards compatibility with app internals)
  const category = tags.length > 0 ? tags[0] : clipboardTask.category;

  const { attachedDocs, docTicks, docFiles } = cloneAttachedDocsFromClipboard(clipboardTask);

  const now = new Date().toISOString();

  return normalizeTaskEstimatedDuration({
    // Required fields with sensible defaults
    id: newId,
    name: clipboardTask.name || '',
    order: index + 1,
    groupId: clipboardTask.groupId || null,
    dependsOn: [],
    unlockConditions: [],

    // Status - 'pending' is the default for new tasks (note: app uses 'pending', not 'todo')
    status: clipboardTask.status || 'pending',

    // Position will be set by the paste handler
    x: undefined,
    y: undefined,

    // Timestamps
    createdAt: now,
    updatedAt: now,
    startedAt: clipboardTask.startedAt || null,
    completedAt: clipboardTask.completedAt || null,

    // Optional fields - only include if present
    priority: clipboardTask.priority,
    difficulty: clipboardTask.difficulty,
    nodeType: nodeType,
    category: category, // Derived from tags[0]
    tags: tags.length > 0 ? tags : undefined,
    icon: clipboardTask.icon,
    notes: clipboardTask.notes,
    internalNotes: clipboardTask.internalNotes ?? clipboardTask.externalNotes,
    dueDate: clipboardTask.dueDate || null,
    estimatedDurationMinutes: syncEstimatedMinutesFromClipboardPayload(clipboardTask),
    reminders: (() => {
      const list = normalizeReminders(clipboardTask.reminders).map((r) => restartRecurringReminder({
        ...r,
        id: newReminderId(),
        createdAt: now,
        askedAt: undefined,
      }, Number(now) || Date.now()));
      return list.length ? list : undefined;
    })(),
    customFieldValues: remappedCustomFieldValues,

    // Nested items
    subtasks: clonedSubtasks.length > 0 ? clonedSubtasks : undefined,
    deliverables: clonedDeliverables.length > 0 ? clonedDeliverables : undefined,
    inputs: clonedInputs.length > 0 ? clonedInputs : undefined,

    // Attached docs (by reference) and their per-mission checkbox overrides
    attachedDocs,
    docTicks,
    docFiles,
    variableValues: cloneMissionVariableValues(clipboardTask),
    // If the source mission already went through the legacy notes→docs
    // migration, the clone must be born migrated too — otherwise opening it
    // re-migrates the copied legacy `notes` into a fresh stale doc
    docsMigratedAt: clipboardTask.docsMigrated ? now : undefined,

    waitFor: nodeType === 'wait' ? (clipboardTask.waitFor || 'email') : undefined,
    waitDetails: nodeType === 'wait' ? (clipboardTask.waitDetails || '') : undefined,
    fulfilledAt: nodeType === 'wait' ? null : undefined,
    // Decision node specific
    options: clonedOptions,
    // Remap answer/selectedOption from option index to new option ID
    answer: (clipboardTask.answer !== undefined && clipboardTask.answer !== null)
      ? (optionIdMap.get(`${index}-${clipboardTask.answer}`) || null)
      : null,
    selectedOption: (clipboardTask.selectedOption !== undefined && clipboardTask.selectedOption !== null)
      ? (optionIdMap.get(`${index}-${clipboardTask.selectedOption}`) || undefined)
      : undefined,

    // Internal fields for paste processing
    _clipboardPos: { x: pos[0], y: pos[1] },
    _needsNestedSprint: clipboardTask.hasNestedSprint || false,
    _clipboardIndex: index,
    _dependsOnRaw: clipboardTask.dependsOn,
    _unlockConditionsRaw: clipboardTask.unlockConditions,
    // Milestone host attachment (host task index) — resolved to the host's new
    // ID in remapDependencies, once all tasks in the set have been cloned
    _milestoneAnchorRaw: (nodeType === 'milestone' && typeof clipboardTask.milestoneAnchor?.task === 'number')
      ? clipboardTask.milestoneAnchor
      : undefined,
    _noteAnchorRaw: (nodeType === 'note' && typeof clipboardTask.noteAnchor?.task === 'number')
      ? clipboardTask.noteAnchor
      : undefined,
  });
}
